<template>
  <v-container>
    <v-skeleton-loader
      v-if="loading"
      type="table"
    />

    <div v-else>
      <v-data-table
        v-sortable-data-table
        :headers="headers"
        :items="items"
        :footer-props="{ itemsPerPageText: '行/ページ:' }"
        no-data-text="データがありません"
        @sorted="onSorted"
      >
        <template #top>
          <v-toolbar
            flat
            color="transparent"
          >
            <v-btn
              color="secondary"
              :ripple="false"
              small
              class="ml-2"
              @click="$emit('open-formula-items')"
            >
              計測項目管理
            </v-btn>
            <v-spacer />
            <v-btn
              color="primary"
              :ripple="false"
              small
              @click="openCreateDialog"
            >
              + セクション追加
            </v-btn>
          </v-toolbar>
        </template>

        <template #[`item.drag`]>
          <v-icon
            size="18"
            class="cursor-grab"
            color="grey"
          >
            {{ icons.mdiDragVertical }}
          </v-icon>
        </template>

        <template #[`item.attributes.metricNames`]="{ item }">
          <v-chip
            v-for="(name, idx) in item.attributes.metricNames"
            :key="item.attributes.metricIds[idx]"
            x-small
            class="mr-1 cursor-pointer"
            @click="$emit('metric-click', { metricId: item.attributes.metricIds[idx], metricName: name })"
          >
            {{ name }}
          </v-chip>
        </template>

        <template #[`item.actions`]="{ item }">
          <v-btn
            icon
            :ripple="false"
            @click="openEditDialog(item)"
          >
            <v-icon small>
              {{ icons.mdiPencilOutline }}
            </v-icon>
          </v-btn>
          <v-dialog max-width="400">
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                :ripple="false"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon small>
                  {{ icons.mdiTrashCanOutline }}
                </v-icon>
              </v-btn>
            </template>
            <template #default="dialog">
              <v-card>
                <v-card-title>削除の確認</v-card-title>
                <v-card-text>
                  このセクションを削除しますか？この操作は取り消せません。
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    text
                    small
                    @click="dialog.value = false"
                  >
                    キャンセル
                  </v-btn>
                  <v-btn
                    color="error"
                    small
                    @click="destroyItem(item.id); dialog.value = false"
                  >
                    削除
                  </v-btn>
                </v-card-actions>
              </v-card>
            </template>
          </v-dialog>
        </template>
      </v-data-table>
    </div>

    <v-dialog
      v-model="editDialog"
      max-width="600"
      @click:outside="resetParams"
    >
      <template #default="dialog">
        <v-card>
          <v-card-title class="d-flex align-center justify-space-between px-8 pt-6">
            {{ isNew ? '新規作成' : '編集' }}
            <v-btn
              color="secondary"
              :ripple="false"
              small
              @click="$emit('open-metrics')"
            >
              + 指標追加
            </v-btn>
          </v-card-title>
          <v-card-text class="px-8 pb-8">
            <v-row>
              <v-col cols="12" class="pt-6">
                <v-text-field
                  v-model="params.label"
                  label="名前"
                  autofocus
                />
              </v-col>
              <v-col cols="12" class="pt-0 mt-n4">
                <v-autocomplete
                  v-model="metricToAdd"
                  :items="availableMetrics"
                  item-text="name"
                  item-value="id"
                  label="指標を追加"
                  clearable
                  no-data-text="追加できる指標がありません"
                  @change="addMetric"
                />
              </v-col>
              <v-col v-if="selectedMetricsOrdered.length" cols="12">
                <v-data-table
                  v-sortable-data-table
                  :headers="metricHeaders"
                  :items="selectedMetricsOrdered"
                  hide-default-footer
                  hide-default-header
                  :items-per-page="-1"
                  no-data-text=""
                  class="elevation-0"
                  style="border: 1px solid rgba(255,255,255,0.12); border-radius: 4px; max-height: 240px; overflow-y: auto;"
                  @sorted="onMetricSorted"
                >
                  <template #[`item.row`]="{ item }">
                    <div class="d-flex align-center">
                      <v-icon size="18" class="cursor-grab mr-3" color="grey">
                        {{ icons.mdiDragVertical }}
                      </v-icon>
                      <span class="flex-grow-1">{{ item.name }}</span>
                      <v-btn icon small :ripple="false" @click.stop="$emit('metric-click', { metricId: item.id, metricName: item.name })">
                        <v-icon small>{{ icons.mdiPencilOutline }}</v-icon>
                      </v-btn>
                      <v-btn icon small :ripple="false" @click.stop="removeMetric(item.id)">
                        <v-icon small>{{ icons.mdiTrashCanOutline }}</v-icon>
                      </v-btn>
                    </div>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions class="px-8 pb-6">
            <v-spacer />
            <v-btn
              text
              small
              @click="closeDialog(dialog)"
            >
              キャンセル
            </v-btn>
            <v-btn
              color="primary"
              small
              :disabled="!params.label"
              @click="saveItem(dialog)"
            >
              {{ isNew ? '作成' : '更新' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </template>
    </v-dialog>
  </v-container>
</template>

<script>
import {
  ref,
  reactive,
  computed,
  nextTick,
  onMounted,
} from '@vue/composition-api'
import { mdiPencilOutline, mdiTrashCanOutline, mdiDragVertical, mdiClose } from '@mdi/js'
import ReportSectionApi from '@/api/v1/admin/ReportSection'
import sortableDataTable from '@/directives/sortableDataTable'

export default {
  directives: { sortableDataTable },
  setup(props, { emit }) {
    const items = ref([])
    const allMetrics = ref([])
    const loading = ref(false)
    const editDialog = ref(false)
    const metricToAdd = ref(null)
    const params = reactive({
      id: null,
      label: '',
      metricIds: [],
    })

    const isNew = computed(() => params.id === null)

    const availableMetrics = computed(() =>
      allMetrics.value.filter(m => !params.metricIds.includes(m.id))
    )

    const selectedMetricsOrdered = computed(() =>
      params.metricIds.map(id => allMetrics.value.find(m => m.id === id)).filter(Boolean)
    )

    const metricHeaders = [
      { text: '', value: 'row', sortable: false },
    ]

    const addMetric = id => {
      if (id && !params.metricIds.includes(id)) {
        params.metricIds.push(id)
      }
      nextTick(() => { metricToAdd.value = null })
    }

    const removeMetric = id => {
      const idx = params.metricIds.indexOf(id)
      if (idx !== -1) params.metricIds.splice(idx, 1)
    }

    const onMetricSorted = event => {
      const moved = params.metricIds.splice(event.oldIndex, 1)[0]
      params.metricIds.splice(event.newIndex, 0, moved)
    }

    const headers = [
      {
        text: '', value: 'drag', sortable: false, width: '40px',
      },
      { text: '名前', value: 'attributes.label' },
      { text: '指標', value: 'attributes.metricNames', sortable: false },
      {
        text: '', value: 'actions', sortable: false, align: 'end',
      },
    ]

    const fetchItems = async () => {
      loading.value = true
      try {
        const res = await ReportSectionApi.getList()
        items.value = res.data.reportSections?.data || []
        allMetrics.value = res.data.meta?.metrics || []
        emit('loaded', items.value.length)
      } catch (e) {
        // TODO
      } finally {
        loading.value = false
      }
    }

    const resetParams = () => {
      params.id = null
      params.label = ''
      params.metricIds = []
      metricToAdd.value = null
    }

    const openCreateDialog = () => {
      resetParams()
      editDialog.value = true
    }

    const openEditDialog = item => {
      params.id = item.id
      params.label = item.attributes.label
      params.metricIds = item.attributes.metricIds || []
      editDialog.value = true
    }

    const closeDialog = dialog => {
      // eslint-disable-next-line no-param-reassign
      dialog.value = false
      resetParams()
    }

    const saveItem = async dialog => {
      try {
        if (isNew.value) {
          await ReportSectionApi.create({ label: params.label, metric_ids: params.metricIds })
        } else {
          await ReportSectionApi.update(params.id, { label: params.label, metric_ids: params.metricIds })
        }
        closeDialog(dialog)
        fetchItems()
      } catch (e) {
        // TODO
      }
    }

    const destroyItem = async id => {
      try {
        await ReportSectionApi.destroy(id)
        await fetchItems()
      } catch (e) {
        // TODO
      }
    }

    const onSorted = async event => {
      const moved = items.value.splice(event.oldIndex, 1)[0]
      items.value.splice(event.newIndex, 0, moved)
      const positions = items.value.map((item, idx) => ({ id: item.id, position: idx }))
      try {
        await ReportSectionApi.updatePositions({ positions })
      } catch (e) {
        // TODO
      }
    }

    onMounted(fetchItems)

    return {
      items,
      allMetrics,
      loading,
      fetchItems,
      editDialog,
      metricToAdd,
      params,
      isNew,
      availableMetrics,
      selectedMetricsOrdered,
      metricHeaders,
      headers,
      addMetric,
      removeMetric,
      onMetricSorted,
      openCreateDialog,
      openEditDialog,
      closeDialog,
      saveItem,
      destroyItem,
      onSorted,
      resetParams,
      icons: {
        mdiPencilOutline,
        mdiTrashCanOutline,
        mdiDragVertical,
        mdiClose,
      },
    }
  },
}
</script>
